import { t } from "i18next";
import * as z from "zod";

import {
  ACCOUNT_ROLES,
  AMOUNT_BASIS,
  POSTING_DIRECTIONS,
} from "@/features/shared/api/enums";

/**
 * Schéma du formulaire d'un modèle d'imputation
 * (`POST /PostingTemplates/add`, guide §6.4) — le modèle naît en DRAFT et
 * décrit, pour un code produit et un provider, les lignes de débit/crédit
 * à générer et leur base de montant. Au moins une ligne exigée.
 *
 * Factory (règle projet) : créé via `createPostingTemplateSchema()` et
 * mémoïsé au niveau composant sur la langue courante.
 */
export const createPostingTemplateSchema = () =>
  z.object({
    productCodeId: z
      .string()
      .trim()
      .uuid({ message: t("invalidFieldError") })
      .or(z.literal(""))
      .optional(),
    providerId: z
      .string()
      .trim()
      .uuid({ message: t("invalidFieldError") })
      .or(z.literal(""))
      .optional(),
    version: z
      .number({ invalid_type_error: t("invalidFieldError") })
      .int({ message: t("invalidFieldError") })
      .min(1, { message: t("invalidFieldError") }),
    effectiveFrom: z
      .string()
      .trim()
      .min(1, { message: t("requiredFieldError") }),
    effectiveTo: z.string().trim().or(z.literal("")).optional(),
    lines: z
      .array(
        z.object({
          sequence: z
            .number({ invalid_type_error: t("invalidFieldError") })
            .int({ message: t("invalidFieldError") })
            .min(1, { message: t("invalidFieldError") }),
          accountRole: z.enum(ACCOUNT_ROLES, {
            errorMap: () => ({ message: t("requiredFieldError") }),
          }),
          accountId: z
            .string()
            .trim()
            .uuid({ message: t("invalidFieldError") }),
          direction: z.enum(POSTING_DIRECTIONS, {
            errorMap: () => ({ message: t("requiredFieldError") }),
          }),
          amountBasis: z.enum(AMOUNT_BASIS, {
            errorMap: () => ({ message: t("requiredFieldError") }),
          }),
        })
      )
      .min(1, { message: t("requiredFieldError") }),
  });

export type PostingTemplateFormValues = z.infer<
  ReturnType<typeof createPostingTemplateSchema>
>;
